import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Github, ArrowRight, Shield, Info, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const Projects = ({ projects }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Resolve translated project text with fallback to mock data
  const pt = (project, field) => {
    if (!project.translationKey) return project[field];
    const key = `projects.${project.translationKey}.${field}`;
    const val = t(key);
    return val === key ? project[field] : val;
  };
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollReveal();
  const { ref: otherRef, isVisible: otherVisible } = useScrollReveal();
  const featuredProjects = projects.filter(p => p.featured);
  const otherProjects = projects.filter(p => !p.featured);

  // Close modal on Escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setSelectedProject(null);
  }, []);

  useEffect(() => {
    if (selectedProject) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedProject, handleKeyDown]);

  const ProjectCard = ({ project, featured = false }) => (
    <Card
      className={`overflow-hidden bg-card dark:bg-card border border-border/50 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group ${
        featured ? 'lg:col-span-1' : ''
      }`}
      onClick={() => setSelectedProject(project)}
    >
      <div
        className="aspect-video overflow-hidden flex items-center justify-center relative"
        style={{ backgroundColor: project.imageBgColor || 'transparent' }}
      >
        {project.imageContain ? (
          <>
            <img
              src={project.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover scale-150 blur-2xl opacity-60"
            />
            <div
              className="absolute inset-0"
              style={{
                background: `radial-gradient(circle at center, transparent 20%, ${project.imageBgColor || 'transparent'} 80%)`
              }}
            />
            <img
              src={project.image}
              alt={pt(project, 'title')}
              className="relative z-10 w-auto h-3/4 object-contain group-hover:scale-105 transition-transform duration-500"
              style={{
                filter: 'drop-shadow(0 0 25px rgba(59, 130, 246, 0.3))',
                maskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 85%)',
                WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 85%)'
              }}
            />
          </>
        ) : (
          <img
            src={project.image}
            alt={pt(project, 'title')}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        )}
      </div>
      <div className="p-6">
        <h3 className={`font-semibold text-foreground mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ${
          featured ? 'text-xl' : 'text-lg'
        }`}>
          {pt(project, 'title')}
        </h3>
        <p className="text-muted-foreground mb-4 line-clamp-2">
          {pt(project, 'description')}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {project.tags.map((tag, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-0 text-xs"
            >
              {tag}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex space-x-3">
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <Github className="w-4 h-4 mr-1" />
              <span className="text-sm">{t('projects.code')}</span>
            </a>
            {project.demo && (
              <a
                href={project.demo}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                <span className="text-sm">{t('projects.demo')}</span>
              </a>
            )}
            {project.aboutPage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(project.aboutPage);
                }}
                className="flex items-center text-muted-foreground hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              >
                <Info className="w-4 h-4 mr-1" />
                <span className="text-sm">{t('projects.about')}</span>
              </button>
            )}
            {project.privacyPolicy && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(project.privacyPolicy);
                }}
                className="flex items-center text-muted-foreground hover:text-green-600 dark:hover:text-green-400 transition-colors"
              >
                <Shield className="w-4 h-4 mr-1" />
                <span className="text-sm">{t('projects.privacy')}</span>
              </button>
            )}
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </Card>
  );

  return (
    <section id="projects" className="py-20 md:py-32 bg-background dark:bg-background">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-4 tracking-wide uppercase">
            {t('projects.title')}
          </span>
          <h2 className="text-3xl md:text-5xl font-light text-foreground mb-4">
            {t('projects.title')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-teal-500 mx-auto rounded-full mb-8" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('projects.subtitle')}
          </p>
        </div>

        {/* Featured Projects */}
        <div ref={featuredRef} className={`grid lg:grid-cols-2 gap-6 mb-12 reveal-up ${featuredVisible ? 'visible' : ''}`}>
          {featuredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              featured={true}
            />
          ))}
        </div>

        {/* Other Projects */}
        {otherProjects.length > 0 && (
          <div ref={otherRef} className={`reveal-up ${otherVisible ? 'visible' : ''}`}>
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-foreground text-center mb-8">
                {t('projects.otherProjects')}
              </h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-16 p-8 rounded-2xl bg-gradient-to-r from-blue-500/5 to-teal-500/5 border border-border/50">
          <p className="text-muted-foreground mb-6">
            {t('projects.viewMorePrompt')}
          </p>
          <Button
            onClick={() => window.open('https://github.com/tabkiliamov', '_blank')}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25"
          >
            <Github className="w-4 h-4 mr-2" />
            {t('projects.viewAllGithub')}
          </Button>
        </div>
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeInUp"
          style={{ animationDuration: '0.2s' }}
          onClick={() => setSelectedProject(null)}
        >
          <Card
            className="max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-card dark:bg-card border border-border/50 shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedProject(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div
              className="aspect-video overflow-hidden flex items-center justify-center relative"
              style={{ backgroundColor: selectedProject.imageBgColor || 'transparent' }}
            >
              {selectedProject.imageContain ? (
                <>
                  <img
                    src={selectedProject.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover scale-150 blur-2xl opacity-60"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `radial-gradient(circle at center, transparent 20%, ${selectedProject.imageBgColor || 'transparent'} 80%)`
                    }}
                  />
                  <img
                    src={selectedProject.image}
                    alt={pt(selectedProject, 'title')}
                    className="relative z-10 w-auto h-3/4 object-contain"
                    style={{
                      filter: 'drop-shadow(0 0 25px rgba(59, 130, 246, 0.3))',
                      maskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 85%)',
                      WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at center, black 40%, transparent 85%)'
                    }}
                  />
                </>
              ) : (
                <img
                  src={selectedProject.image}
                  alt={pt(selectedProject, 'title')}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-semibold text-foreground mb-4">
                {pt(selectedProject, 'title')}
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {pt(selectedProject, 'description')}
              </p>

              <div className="flex flex-wrap gap-2 mb-6">
                {selectedProject.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-0"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => window.open(selectedProject.github, '_blank')}
                  variant="outline"
                  className="border-border hover:border-blue-300 dark:hover:border-blue-700"
                >
                  <Github className="w-4 h-4 mr-2" />
                  {t('projects.viewCode')}
                </Button>
                {selectedProject.demo && (
                  <Button
                    onClick={() => window.open(selectedProject.demo, '_blank')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('projects.liveDemo')}
                  </Button>
                )}
                {selectedProject.aboutPage && (
                  <Button
                    onClick={() => {
                      setSelectedProject(null);
                      navigate(selectedProject.aboutPage);
                    }}
                    variant="outline"
                    className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                  >
                    <Info className="w-4 h-4 mr-2" />
                    {t('projects.aboutProject')}
                  </Button>
                )}
                {selectedProject.privacyPolicy && (
                  <Button
                    onClick={() => {
                      setSelectedProject(null);
                      navigate(selectedProject.privacyPolicy);
                    }}
                    variant="outline"
                    className="border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {t('projects.privacyPolicy')}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </section>
  );
};

export default Projects;
